import { useState } from "react";
import { CalendarPlus } from "lucide-react";
import { Button, Card, CardTitle, Select } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";
import { useDepartments } from "../hooks/useDepartments";

// Subscribe to Clevis jobs in Google Calendar, Outlook or an iPhone: a private
// link the calendar checks every half hour or so. "Mine" for anyone; managers
// can subscribe to everyone's jobs or one department's.

export function CalendarLinks({ tenantId, manager = false }: { tenantId: string; manager?: boolean }) {
  const dept = useDepartments();
  const [scope, setScope] = useState("mine");
  const [link, setLink] = useState<{ url: string; webcal: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  async function get(reset = false) {
    setBusy(true);
    setNote("");
    try {
      const r = await call<{ url: string; webcal: string }>("getCalendarLink", { tenantId, scope, reset });
      setLink(r);
      if (reset) setNote("New link made. The old one stops working; re-subscribe with this one.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const scopeName = scope === "mine" ? "My jobs" : scope === "all" ? "All jobs" : dept.departments.find((d) => `dept:${d.id}` === scope)?.name ?? "Department";

  return (
    <Card className="space-y-2">
      <div className="flex items-center gap-2">
        <CalendarPlus size={16} className="text-steel" />
        <CardTitle className="mb-0">See jobs in your calendar</CardTitle>
      </div>
      <p className="text-xs text-steel">Subscribe once in Google Calendar, Outlook or your iPhone; jobs show up and stay current (calendars check every 30 minutes or so). The link is private: don't share it.</p>
      <div className="flex flex-wrap items-center gap-2">
        {manager ? (
          <Select className="w-52 py-1 text-sm" value={scope} onChange={(e) => { setScope(e.target.value); setLink(null); }} aria-label="Which jobs">
            <option value="mine">My jobs</option>
            <option value="all">All jobs</option>
            {dept.departments.map((d) => (
              <option key={d.id} value={`dept:${d.id}`}>
                {d.name}
              </option>
            ))}
          </Select>
        ) : null}
        {!link ? (
          <Button busy={busy} onClick={() => void get()}>
            Get my calendar link
          </Button>
        ) : null}
      </div>
      {link ? (
        <div className="space-y-2 rounded-lg bg-cream/60 p-2 text-xs">
          <div className="flex items-center gap-2">
            <input readOnly className="min-w-0 flex-1 rounded border border-steel/30 bg-white px-2 py-1 font-mono text-[11px]" value={link.url} onFocus={(e) => e.target.select()} />
            <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => void navigator.clipboard.writeText(link.url).then(() => setNote("Copied."))}>
              Copy
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <a className="rounded border border-steel/30 bg-white px-2 py-1 text-ink hover:border-steel" href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(link.webcal)}`} target="_blank" rel="noreferrer">
              Add to Google Calendar
            </a>
            <a className="rounded border border-steel/30 bg-white px-2 py-1 text-ink hover:border-steel" href={`https://outlook.office.com/calendar/addfromweb?url=${encodeURIComponent(link.url)}&name=${encodeURIComponent(`Clevis: ${scopeName}`)}`} target="_blank" rel="noreferrer">
              Add to Outlook
            </a>
            <a className="rounded border border-steel/30 bg-white px-2 py-1 text-ink hover:border-steel" href={link.webcal}>
              iPhone / Mac Calendar
            </a>
            <button className="ml-auto text-steel underline hover:text-ink" onClick={() => void get(true)} disabled={busy}>
              Make a new link
            </button>
          </div>
        </div>
      ) : null}
      {note ? <p className="text-xs text-steel">{note}</p> : null}
    </Card>
  );
}
